import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { acceptInvite, handleAuthCallback, login, oauthLogin, requestPasswordRecovery, updateUser } from "@netlify/identity";
import { ArrowLeft, KeyRound, Loader2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ADMIN_EMAIL = "info@geauxfotos.com";

function errorMessage(error, fallback) {
  if (error?.status === 401) return "Invalid email or password.";
  if (error?.status === 404) return "No admin account was found for that email.";
  return error?.message || fallback;
}

export default function AdminLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(ADMIN_EMAIL);
  const [inviteToken, setInviteToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await handleAuthCallback();
        if (!active || !result) return;
        if (result.type === "recovery") {
          setMode("new-password");
          setMessage("Recovery link verified. Choose your new password.");
        } else if (result.type === "invite") {
          setInviteToken(result.token);
          setMode("new-password");
          setMessage("Admin invitation verified. Choose your password.");
        } else if (["oauth", "confirmation"].includes(result.type)) {
          nav("/admin", { replace: true });
        }
      } catch (callbackError) {
        if (active) setError(errorMessage(callbackError, "The authentication link is invalid or expired."));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [nav]);

  const submitLogin = async (event) => {
    event.preventDefault();
    setLoading(true); setError(""); setMessage("");
    try {
      await login(email.trim(), password);
      nav("/admin");
    } catch (loginError) {
      setError(errorMessage(loginError, "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = () => {
    setError("");
    try {
      oauthLogin("google");
    } catch (oauthError) {
      setError(errorMessage(oauthError, "Google sign-in is not available right now."));
    }
  };

  const sendRecovery = async (event) => {
    event.preventDefault();
    setLoading(true); setError(""); setMessage("");
    try {
      await requestPasswordRecovery(email.trim());
      setMessage("Reset requested for info@geauxfotos.com. Check Inbox and Spam for the Netlify Identity recovery email.");
    } catch (recoveryError) {
      setError(errorMessage(recoveryError, "Could not send the reset email."));
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setError(""); setMessage("");
    if (password.length < 10) return setError("Use at least 10 characters.");
    if (password !== confirmPassword) return setError("The passwords do not match.");
    setLoading(true);
    try {
      if (inviteToken) await acceptInvite(inviteToken, password);
      else await updateUser({ password });
      setMessage("Password updated. Opening Cloud Studio…");
      window.setTimeout(() => nav("/admin"), 700);
    } catch (resetError) {
      setError(errorMessage(resetError, "Could not update the password."));
    } finally {
      setLoading(false);
    }
  };

  if (loading && mode === "login") {
    return <div className="min-h-screen bg-[#090909] text-[#C8FF00] grid place-items-center"><Loader2 className="animate-spin" /></div>;
  }

  const isRecovery = mode === "recovery";
  const isNewPassword = mode === "new-password";

  return (
    <div className="min-h-screen bg-[#090909] text-[#F5F0E8] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-sm">
        <p className="font-accent tracking-[0.3em] text-[#C8FF00] text-sm">FOTOS ON THE GEAUX</p>
        <h1 className="font-display text-4xl tracking-tighter mt-2">
          {isRecovery ? "Reset Password" : isNewPassword ? "Choose New Password" : "Admin Access"}
        </h1>
        <p className="mt-3 text-sm leading-6 text-[#F5F0E8]/55">
          {isRecovery ? "Enter your admin email and we’ll send a secure recovery link." : isNewPassword ? "Create a strong password for your Fotos admin account." : "Sign in to manage the Higgsfield Cloud Studio."}
        </p>

        <form onSubmit={isRecovery ? sendRecovery : isNewPassword ? savePassword : submitLogin} className="mt-8 space-y-4" data-testid="admin-login-form">
          {!isNewPassword && <div><Label className="text-xs text-[#F5F0E8]/50">Email</Label><Input data-testid="admin-email" type="email" required value={email} readOnly className="bg-transparent border-white/15 text-[#F5F0E8]" autoComplete="email" /></div>}
          {!isRecovery && <div><Label className="text-xs text-[#F5F0E8]/50">{isNewPassword ? "New password" : "Password"}</Label><Input data-testid="admin-password" type="password" required value={password} onChange={(event) => setPassword(event.target.value)} className="bg-transparent border-white/15 text-[#F5F0E8]" autoComplete={isNewPassword ? "new-password" : "current-password"} /></div>}
          {isNewPassword && <div><Label className="text-xs text-[#F5F0E8]/50">Confirm new password</Label><Input type="password" required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} className="bg-transparent border-white/15 text-[#F5F0E8]" autoComplete="new-password" /></div>}
          {error && <p className="rounded-md border border-[#E32636]/30 bg-[#E32636]/10 p-3 text-sm text-[#ff7b86]">{error}</p>}
          {message && <p className="rounded-md border border-[#39E75F]/30 bg-[#39E75F]/10 p-3 text-sm text-[#7dff96]">{message}</p>}
          <button type="submit" disabled={loading} className="w-full py-3 font-accent tracking-widest bg-[#C8FF00] text-[#090909] rounded-md hover:bg-[#d8ff4a] flex items-center justify-center gap-2 disabled:opacity-50">{loading ? <Loader2 className="animate-spin" size={16} /> : isRecovery ? <><Mail size={16} /> SEND RESET LINK</> : isNewPassword ? <><KeyRound size={16} /> SAVE NEW PASSWORD</> : "SIGN IN"}</button>
        </form>

        {!isRecovery && !isNewPassword && <><div className="my-5 flex items-center gap-3"><span className="h-px flex-1 bg-white/10"/><span className="text-xs text-white/35">OR</span><span className="h-px flex-1 bg-white/10"/></div><button type="button" onClick={signInWithGoogle} className="w-full py-3 border border-white/15 rounded-md bg-white text-black font-medium hover:bg-white/90">Continue with Google</button></>}

        {!isNewPassword && <button type="button" onClick={() => { setMode(isRecovery ? "login" : "recovery"); setError(""); setMessage(""); }} className="mt-5 flex w-full items-center justify-center gap-2 text-sm text-[#F5F0E8]/60 hover:text-[#C8FF00]"><ArrowLeft size={14} /> {isRecovery ? "Back to sign in" : "Forgot password?"}</button>}
      </div>
    </div>
  );
}
